'use client'

import { useEffect } from 'react'

/**
 * How long between service worker update checks (ms).
 * The browser also checks on navigation and every 24h automatically,
 * but explicit checks ensure timely updates in standalone PWA mode
 * where the user may not "navigate" in the traditional sense.
 */
const SW_UPDATE_INTERVAL = 60 * 60 * 1000 // 1 hour

/**
 * Registers the service worker and manages its update lifecycle.
 *
 * **Registration**: Ensures the SW is always registered (not just when
 * notifications are enabled), which is required for PWA installability.
 *
 * **Update lifecycle**: In standalone PWA mode, the app window may never
 * fully close, so a new service worker can sit in "waiting" state
 * indefinitely. This component:
 *
 * 1. Periodically checks for SW updates
 * 2. When a new SW is found waiting, sends it a SKIP_WAITING message
 *    so it activates immediately
 * 3. Listens for `controllerchange` and reloads the page so the new
 *    SW takes full control
 *
 * The reload is safe because we don't serve cached assets — the browser
 * always fetches from the network. When offline caching is added later,
 * this flow ensures users always get the latest cached assets too.
 */
export function PWARegister() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }

    // Don't register service workers in development — they intercept
    // Turbopack's HMR WebSocket and cause infinite compilation loops
    if (process.env.NODE_ENV !== 'production') {
      // Unregister any previously-registered SW from dev sessions
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister()
        }
      })
      return
    }

    let updateInterval: ReturnType<typeof setInterval> | null = null

    // When a new SW takes control, reload so the page uses the new SW.
    // This fires after skipWaiting() succeeds on a waiting SW.
    const onControllerChange = () => {
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange
    )

    /**
     * When a new SW reaches the "installed" (waiting) state, tell it to
     * activate immediately. Without this, it waits until all windows close.
     */
    function activateWaitingSW(sw: ServiceWorker) {
      if (sw.state === 'installed') {
        sw.postMessage({ type: 'SKIP_WAITING' })
      }
    }

    /**
     * Monitor a newly discovered SW through its state transitions.
     * We wait for it to reach "installed" before sending SKIP_WAITING.
     */
    function trackInstalling(sw: ServiceWorker) {
      sw.addEventListener('statechange', () => {
        activateWaitingSW(sw)
      })
    }

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // If there's already a SW waiting (e.g. from a previous visit), activate it
        if (registration.waiting) {
          activateWaitingSW(registration.waiting)
        }

        // When the browser finds a new SW version, track its installation
        registration.addEventListener('updatefound', () => {
          const newSW = registration.installing
          if (newSW) {
            trackInstalling(newSW)
          }
        })

        // Periodically check for SW updates.
        // The browser checks on navigation + every 24h, but in standalone
        // PWA mode the user may keep the app open for days without a
        // "navigation" event, so explicit checks ensure timely updates.
        updateInterval = setInterval(() => {
          registration.update().catch(() => {
            // Update check failed (offline, server error, etc.) — not critical
          })
        }, SW_UPDATE_INTERVAL)
      })
      .catch(() => {
        // Service worker registration is not critical for app function
      })

    return () => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange
      )
      if (updateInterval) {
        clearInterval(updateInterval)
      }
    }
  }, [])

  return null
}
